import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ReactNode } from 'react';
import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { SBHorizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import type { CardHit } from '@sorare/core/src/lib/algolia';

import { CardRowFromHit as CardRow } from '#src/components/card/CardRow/index.tsx';
import { TokenTransferValidator } from '#src/components/token/TokenTransferValidator/index.tsx';

import type { OfferSide_anyCard } from './__generated__/index.graphql.ts';
import { messages } from './i18n.ts';

type CardDataProvider = {
  cards: CardHit[];
  cardsData: Record<string, OfferSide_anyCard>;
};

type Props = CardDataProvider & {
  title: ReactNode;
  toggleAddCardOpened: () => void;
  setCards: (cards: CardHit[]) => Promise<boolean>;
  children?: ReactNode;
  displayMinPrices?: boolean;
  isOwnSide?: boolean;
  addCardDisabledWarning?: ReactNode;
};

type SelectedCardProps = {
  card: CardHit;
  cardData: OfferSide_anyCard;
  displayMinPrices: boolean;
  removeCard: (card: CardHit) => void;
  children?: ReactNode;
};

const Section = styled(Vertical)`
  padding: var(--double-unit);
`;

const SectionHeader = styled(SBHorizontal)`
  margin-bottom: var(--double--unit);
`;

const CardWrapper = styled.div`
  position: relative;
  background: var(--c-nd-100);
  border-radius: var(--unit);
  border: 1px solid var(--c-nd-200);
`;

const WarningWrapper = styled.div`
  padding: 0 var(--unit) var(--unit);
`;

const SelectedCard = ({
  card,
  cardData,
  displayMinPrices,
  removeCard,
  children,
}: SelectedCardProps) => {
  const onRemove = useCallback(() => removeCard(card), [removeCard, card]);

  return (
    <CardWrapper>
      <CardRow
        card={card}
        cardData={cardData}
        displayMinPrice={displayMinPrices}
        onRemove={onRemove}
      />
      {children}
    </CardWrapper>
  );
};

export const OfferSide = ({
  cards,
  cardsData,
  toggleAddCardOpened,
  setCards,
  title,
  children,
  displayMinPrices = false,
  isOwnSide,
  addCardDisabledWarning,
}: Props) => {
  const removeCard = useCallback(
    (card: CardHit) => {
      setCards(cards.filter(c => c.slug !== card.slug));
    },
    [setCards, cards]
  );

  return (
    <Section>
      <SectionHeader>
        {title}
        <Tooltip
          title={
            addCardDisabledWarning ? (
              <FormattedMessage defaultMessage="This user does not accept card trades." />
            ) : (
              ''
            )
          }
        >
          <Button
            color="tertiary"
            onClick={toggleAddCardOpened}
            size="small"
            disabled={!!addCardDisabledWarning}
          >
            <FormattedMessage {...messages.addCard} />
          </Button>
        </Tooltip>
      </SectionHeader>
      {addCardDisabledWarning}
      {cards.length > 0 && (
        <TokenTransferValidator
          cards={Object.values(cardsData).filter(token =>
            cards.some(card => card.slug === token?.slug)
          )}
          transferContext="send_trade"
          shouldValidate={!!isOwnSide}
        >
          {({ validationMessages }) => (
            <Vertical>
              {cards.map(card => (
                <SelectedCard
                  key={card.slug}
                  card={card}
                  cardData={cardsData[card.slug]}
                  displayMinPrices={displayMinPrices}
                  removeCard={removeCard}
                >
                  {!!validationMessages[card.slug] && (
                    <WarningWrapper>
                      {validationMessages[card.slug]}
                    </WarningWrapper>
                  )}
                </SelectedCard>
              ))}
            </Vertical>
          )}
        </TokenTransferValidator>
      )}
      {children}
    </Section>
  );
};

OfferSide.fragments = {
  anyCard: gql`
    fragment OfferSide_anyCard on AnyCardInterface {
      slug
      ...PriceHistoryTooltip_anyCard
      ...TokenTransferValidator_anyCard
      ...DirectOffer_CardRow_anyCard
    }
    ${CardRow.fragments.anyCard}
    ${TokenTransferValidator.fragments.anyCard}
  ` as TypedDocumentNode<OfferSide_anyCard>,
};
